const PREC = 1000

/**
 * Margem de recebimento a mais tolerada APENAS para produtos em kg.
 * Recebimentos em kg podem aceitar ate 5% acima do valor da AR.
 */
export const KG_ACCEPT_TOLERANCE = 0.05

/**
 * Teto de aceite da AR. Produtos em kg toleram KG_ACCEPT_TOLERANCE a mais
 * (imprecisao de balanca), entao o teto e elevado em 5%.
 */
export const arAcceptCeiling = (expectedQuantity: number, isKg = false): number =>
  (isKg ? expectedQuantity * (1 + KG_ACCEPT_TOLERANCE) : expectedQuantity)

export interface ValidateAcceptedQuantityParams {
  /** Quantidade recebida = avariada + util */
  totalReceived: number;
  /** Valor da AR */
  expectedQuantity: number;
  /** Soma do aceito de todos os lotes do produto */
  totalAccepted: number;
  /** Caixaria do produto */
  quantityPerBox: number;
  /** Fornecedor aceita NFD somente em caixas inteiras (allowPartialReturn && !allowBreakUnitOnInvoiceReturn) */
  enforceBox: boolean;
  /** Rejeitado AR (product.returnQuantity): compoe a base da NFD e das sugestoes */
  returnQuantity?: number;
  /** Produto medido em kg: habilita a margem de tolerancia de recebimento a mais */
  isKg?: boolean;
}

export interface ValidateAcceptedQuantityResult {
  /** Teto de aceite = min(recebido, AR) */
  cap: number;
  isValid: boolean;
  suggestions: { value: number; boxes: number }[];
}

/**
 * Valida a quantidade aceita de um produto contra o teto da AR.
 *
 * Teto universal: o total aceito nunca pode ultrapassar o valor da AR
 * (`cap = min(recebido, AR)`), independente das regras do fornecedor.
 *
 * Quando o fornecedor exige caixaria (`enforceBox`), a devolucao via NFD
 * (`nfd = Rejeitado AR + max(0, esperado - aceito)`, base da nota = esperado + Rejeitado AR)
 * deve ser de caixas inteiras (>= 1 caixa). O excesso acima do `cap` volta FORA da NFD.
 */
export const validateAcceptedQuantity = ({
  totalReceived,
  expectedQuantity,
  totalAccepted,
  quantityPerBox,
  enforceBox,
  returnQuantity = 0,
  isKg = false,
}: ValidateAcceptedQuantityParams): ValidateAcceptedQuantityResult => {
  // Produtos em kg podem aceitar ate 5% acima da AR (recebimento a mais tolerado).
  const cap = Math.min(totalReceived, arAcceptCeiling(expectedQuantity, isKg))

  const capInt = Math.round(cap * PREC)
  const totalAcceptedInt = Math.round(totalAccepted * PREC)

  if (!enforceBox) {
    return {
      cap,
      isValid: totalAccepted === 0 || totalAcceptedInt <= capInt,
      suggestions: [],
    }
  }

  // NFD = Rejeitado AR + max(0, esperado - aceito); base da nota = esperado + Rejeitado AR
  const nfd = returnQuantity + Math.max(0, expectedQuantity - totalAccepted)
  const nfdInt = Math.round(nfd * PREC)
  const boxInt = Math.round(quantityPerBox * PREC)
  const expectedInt = Math.round(expectedQuantity * PREC)
  const returnQtyInt = Math.round(returnQuantity * PREC)

  const nfdBoxValid = nfdInt <= 0 || (boxInt > 0 && nfdInt % boxInt === 0 && nfdInt >= boxInt)

  const isValid = totalAccepted === 0
    || (totalAcceptedInt <= capInt && nfdBoxValid)

  const suggestions: { value: number; boxes: number }[] = []
  if (!isValid && !nfdBoxValid && boxInt > 0) {
    // aceito = (esperado + Rejeitado AR) - K x caixa; encontra os multiplos mais proximos.
    // Sugestoes acima do esperado sao descartadas: aceitar > esperado deixa nfd = Rejeitado AR
    // (a formula linear base - aceito nao vale nesse trecho) e continuaria invalido.
    // Por serem <= esperado <= cap, as sugestoes tambem resolvem o teto da AR
    const baseInt = expectedInt + returnQtyInt
    const floorBoxes = Math.max(1, Math.floor(nfdInt / boxInt))
    const ceilBoxes = Math.ceil(nfdInt / boxInt)

    const suggestAInt = baseInt - floorBoxes * boxInt
    if (suggestAInt >= 0 && suggestAInt <= expectedInt) suggestions.push({ value: suggestAInt / PREC, boxes: floorBoxes })

    if (ceilBoxes !== floorBoxes) {
      const suggestBInt = baseInt - ceilBoxes * boxInt
      if (suggestBInt >= 0 && suggestBInt <= expectedInt) suggestions.push({ value: suggestBInt / PREC, boxes: ceilBoxes })
    }
  }

  return { cap, isValid, suggestions }
}
